/**
 * Registers newly added properties into AddedPropertyReportsWatcherStore when ADD_PROPERTY
 * completes successfully via ProcessTrackerStore (hybrid path alongside list reconciliation).
 *
 * @module store/AddedPropertyProcessBridge
 */

import { PROCESS_TYPE } from "@/store/subscriptionCore/processConstants";
import { IS_DEV } from "@/utils/env";
import { getZillowMessageOutcome } from "@/utils/zillowResponseAdapter";

let _rootStore = null;
/** @type {null | (() => void)} */
let _unsubscribe = null;

/**
 * @param {unknown} records
 * @returns {string[]} Unique non-empty property ids from `id` / `property_id` / `pk`.
 */
export function extractUniquePropertyIdsFromRecords(records) {
  if (!Array.isArray(records)) return [];
  const seen = new Set();
  const out = [];
  for (const row of records) {
    if (row == null || typeof row !== "object") continue;
    const raw = row.id ?? row.property_id ?? row.pk;
    if (raw == null) continue;
    const id = String(raw).trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/**
 * @param {import("@/utils/zillowResponseAdapter").AdaptedZillowResponse | null | undefined} adapted
 * @returns {{ registerIds: string[], unregisterIds: string[] }}
 */
export function resolveReportsWatcherIdsForAdapted(adapted) {
  if (!adapted) return { registerIds: [], unregisterIds: [] };

  const outcome = getZillowMessageOutcome(adapted.message);
  const newIds = extractUniquePropertyIdsFromRecords(adapted.newProperties);
  const existingIds = extractUniquePropertyIdsFromRecords(adapted.existingProperties);

  if (outcome.shouldRegisterReportsWatcher === false) {
    const unregisterIds =
      outcome.propertySelection === "existing" ? existingIds : [...newIds, ...existingIds];
    return { registerIds: [], unregisterIds };
  }

  return { registerIds: newIds, unregisterIds: [] };
}

/**
 * @param {import("@/utils/zillowResponseAdapter").AdaptedZillowResponse | null | undefined} adapted
 * @returns {boolean}
 */
export function shouldRegisterReportsWatcherForAdapted(adapted) {
  return resolveReportsWatcherIdsForAdapted(adapted).registerIds.length > 0;
}

/**
 * @param {Object|null|undefined} rootStore
 */
function handleCompletion(payload) {
  if (!payload || payload.type !== PROCESS_TYPE.ADD_PROPERTY) return;

  const store = _rootStore;
  if (!store) return;

  const customer = String(store.authStore?.customer ?? "").trim();
  if (!customer) return;

  const watcher = store.addedPropertyReportsWatcherStore;
  if (!watcher || typeof watcher.register !== "function") return;

  const adapted = payload.output?.payload?.adapted;
  const { registerIds, unregisterIds } = resolveReportsWatcherIdsForAdapted(adapted);

  if (unregisterIds.length > 0 && typeof watcher.unregister === "function") {
    watcher.unregister(customer, unregisterIds);
  }
  if (registerIds.length > 0) {
    watcher.register(customer, registerIds);
  }
}

/**
 * @param {Object|null|undefined} rootStore
 */
export function start(rootStore) {
  if (_unsubscribe != null) return;

  _rootStore = rootStore ?? null;
  const pts = rootStore?.processTrackerStore;
  if (!pts || typeof pts.subscribeProcessCompletion !== "function") {
    if (IS_DEV) {
      console.warn("[AddedPropertyProcessBridge] missing processTrackerStore.subscribeProcessCompletion");
    }
    _rootStore = null;
    return;
  }

  _unsubscribe = pts.subscribeProcessCompletion(handleCompletion);
}

export function stop() {
  if (typeof _unsubscribe === "function") {
    _unsubscribe();
  }
  _unsubscribe = null;
  _rootStore = null;
}

const AddedPropertyProcessBridge = {
  start,
  stop,
};

export default AddedPropertyProcessBridge;
